import React, { useEffect, useState } from "react";
import UseGetDashBoardData from "../../../../Components/Hooks/useDashboardData";
import NuAmount from "../../../../Components/Utils/NuAmount";
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EC4899", "#6366F1"];

export default function PurchaseOrdersfromCustomers() {
  const { data, loading } = UseGetDashBoardData(`/dashboard/customerOrderStats`);

  const chartData = data?.customer_category_wise?.map((item) => ({
    name: item.category
      ? item.category.charAt(0).toUpperCase() + item.category.slice(1).toLowerCase()
      : "Unknown",
    value: item?.count || 0,
  })) || [];

  const [sumOfChartData, setSumOfChartData] = useState(0);

  useEffect(() => {
    setSumOfChartData(
      data?.customer_category_wise?.reduce((acc, item) => acc + (item?.count || 0), 0) || 0
    );
  }, [data?.customer_category_wise]);

  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.04)] h-full flex flex-col justify-between transition-all duration-300">
      <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">PO Received from Customers</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/50">
            <NuAmount value={data?.total_price || 0} />
          </span>
        </div>
      </div>

      {loading ? (
        <div className="p-4 flex items-center justify-center space-x-4 animate-pulse">
          <div className="w-24 h-24 rounded-full bg-gray-100"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-100 rounded w-full"></div>
            <div className="h-4 bg-gray-100 rounded w-3/4"></div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center flex-1">
          {/* Donut Chart */}
          <div className="md:col-span-5 relative flex justify-center items-center h-48">
            {sumOfChartData > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={68}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                    <Label
                      value={`${data?.total_count || 0}`}
                      position="center"
                      style={{ fontSize: "16px", fontWeight: "bold", fill: "#1F2937" }}
                    />
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      borderRadius: "8px",
                      color: "#FFF",
                      border: "none",
                      fontSize: "11px",
                      padding: "6px 10px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center text-center">
                <span className="text-2xl font-bold text-gray-300">0</span>
                <span className="text-xs text-gray-400">No orders</span>
              </div>
            )}
          </div>

          {/* Category Progress Bars */}
          <div className="md:col-span-7 space-y-2.5 max-h-52 overflow-y-auto pr-1">
            {data?.customer_category_wise?.map((item, index) => {
              const catName = item.category
                ? item.category.charAt(0).toUpperCase() + item.category.slice(1).toLowerCase()
                : "Other";
              const percent = sumOfChartData > 0 ? Math.round(((item.count || 0) / sumOfChartData) * 100) : 0;
              const color = COLORS[index % COLORS.length];

              return (
                <div
                  key={index}
                  className="p-2.5 rounded-xl bg-gray-50/70 hover:bg-gray-100/80 transition-all duration-200"
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center space-x-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}></span>
                      <span className="text-xs font-semibold text-gray-700 truncate max-w-[120px]">
                        {catName}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-bold text-gray-900">{item.count || 0}</span>
                      <span className="text-[10px] text-gray-400 font-medium">{percent}%</span>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full h-1.5 bg-gray-200/60 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${percent}%`, backgroundColor: color }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function NODataChart() {
  return (
    <div className="w-full flex justify-center items-center h-40">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={[{ name: "No Data", value: 100 }]}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={60}
            fill="#F3F4F6"
            dataKey="value"
            stroke="none"
          >
            <Label
              value={0}
              position="center"
              style={{ fontSize: "14px", fontWeight: "bold", fill: "#9CA3AF" }}
            />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
